<template>
    <div class="flex flex-col h-full overflow-y-auto">

        <!-- Empty state -->
        <div
            v-if="!selectedRecord"
            class="flex flex-col items-center justify-center flex-1 text-center p-6"
        >
            <i class="fa-duotone fa-solid fa-address-card text-4xl text-gray-300 mb-3"></i>
            <p class="text-xs text-gray-400">Select a record to see context</p>
        </div>

        <template v-else>

            <!-- Record header -->
            <div class="p-3 border-b border-gray-200">
                <div class="flex items-center gap-3">
                    <!-- Avatar -->
                    <div
                        class="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-700 flex items-center justify-center"
                    >
                        <span class="text-white text-xs font-semibold leading-none">
                            {{ initials }}
                        </span>
                    </div>

                    <!-- Name + type -->
                    <div class="min-w-0">
                        <p class="text-gray-900 text-xs font-semibold truncate">
                            {{ selectedRecord.name }}
                        </p>
                        <p class="text-gray-400 text-xs truncate">
                            {{ selectedRecord.type }}
                            <template v-if="selectedRecord.email"> · {{ selectedRecord.email }}</template>
                        </p>
                    </div>
                </div>

                <!-- Tags -->
                <div class="flex flex-wrap gap-1.5 mt-2">
                    <span
                        v-if="selectedRecord.organization"
                        class="text-xs text-blue-400"
                    >
                        {{ selectedRecord.organization }}
                    </span>
                    <span
                        v-if="related.opportunities && related.opportunities.length > 0"
                        class="text-xs text-green-400"
                    >
                        {{ related.opportunities.length }}
                        {{ related.opportunities.length === 1 ? 'opportunity' : 'opportunities' }}
                    </span>
                </div>

                <!-- Lead context badges -->
                <div
                    v-if="related.lead_context"
                    class="flex flex-wrap gap-1.5 mt-2"
                >
                    <span
                        v-if="related.lead_context.status"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                        :class="leadStatusClass(related.lead_context.status)"
                    >
                        {{ related.lead_context.status }}
                    </span>
                    <span
                        v-if="related.lead_context.program"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
                    >
                        {{ related.lead_context.program }}
                    </span>
                    <span
                        v-if="related.lead_context.campaign_type"
                        class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                    >
                        {{ related.lead_context.campaign_type }}
                    </span>
                </div>
            </div>

            <!-- Activity timeline -->
            <div class="p-3">
                <p class="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">
                    <i class="fa-solid fa-timeline mr-1"></i> Activity Timeline
                </p>

                <!-- Loading skeleton -->
                <div v-if="loadingContext" class="space-y-3">
                    <div
                        v-for="n in 4"
                        :key="n"
                        class="flex items-start gap-2 animate-pulse"
                    >
                        <div class="mt-1 w-2 h-2 rounded-full bg-gray-200 flex-shrink-0"></div>
                        <div class="flex-1 space-y-1">
                            <div class="h-2 bg-gray-200 rounded w-1/3"></div>
                            <div class="h-2 bg-gray-200 rounded w-2/3"></div>
                            <div class="h-2 bg-gray-200 rounded w-1/4"></div>
                        </div>
                    </div>
                </div>

                <!-- Timeline entries -->
                <div v-else-if="activities.length > 0" class="border-l-2 border-gray-200 ml-1 space-y-4">
                    <div
                        v-for="(activity, index) in activities"
                        :key="index"
                        class="relative pl-4"
                    >
                        <!-- Icon -->
                        <span class="absolute -left-[11px] top-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center" :class="activityLabelClass(activity.type)">
                            <i :class="activityIconClass(activity.type)" class="text-[9px]"></i>
                        </span>

                        <!-- Content -->
                        <p
                            class="uppercase font-semibold leading-none mb-0.5"
                            style="font-size: 9px;"
                            :class="activityLabelClass(activity.type)"
                        >
                            {{ activity.type }}
                        </p>
                        <p class="text-gray-700 leading-snug" style="font-size: 11px;">
                            {{ activity.subject }}
                        </p>
                        <p class="text-gray-400 mt-0.5" style="font-size: 10px;">
                            {{ formatDate(activity.date) }}
                        </p>
                    </div>
                </div>

                <!-- Empty -->
                <p v-else class="text-xs text-gray-500">
                    No activity history found
                </p>
            </div>

            <!-- Conversation History -->
            <div v-if="emailThread.length > 0" class="p-3 border-t border-gray-200">
                <p class="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">
                    <i class="fa-solid fa-comments mr-1"></i> Conversation History
                </p>

                <div class="space-y-3">
                    <div
                        v-for="(msg, index) in emailThread"
                        :key="index"
                        class="relative pl-4"
                        :class="msg.direction === 'outbound' ? 'border-l-2 border-blue-200' : 'border-l-2 border-green-200'"
                    >
                        <p
                            class="uppercase font-semibold leading-none mb-0.5"
                            style="font-size: 9px;"
                            :class="msg.direction === 'outbound' ? 'text-blue-500' : 'text-green-500'"
                        >
                            {{ msg.direction === 'outbound' ? '→ Sent' : '← Received' }}
                            · {{ formatDate(msg.date) }}
                        </p>
                        <p class="text-gray-700 leading-snug font-medium" style="font-size: 11px;">
                            {{ msg.subject }}
                        </p>
                        <p class="text-gray-500 mt-0.5 line-clamp-3" style="font-size: 10px;">
                            {{ msg.body }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Donation Summary -->
            <div v-if="donationSummary" class="p-3 border-t border-gray-200">
                <p class="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">
                    <i class="fa-solid fa-hand-holding-dollar mr-1"></i> Fundraising History
                </p>

                <div class="rounded bg-gray-50 p-2 space-y-1.5">
                    <div class="flex items-center justify-between">
                        <span class="text-gray-500 text-xs">Total raised</span>
                        <span class="text-green-600 text-xs font-semibold">
                            ${{ Number(donationSummary.total_raised).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-500 text-xs">Campaigns</span>
                        <span class="text-gray-900 text-xs font-medium">
                            {{ donationSummary.campaigns_participated }}
                        </span>
                    </div>
                    <div v-if="donationSummary.most_recent" class="flex items-center justify-between">
                        <span class="text-gray-500 text-xs">Most recent</span>
                        <span class="text-gray-900 text-xs font-medium">
                            ${{ Number(donationSummary.most_recent.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
                            · {{ formatDate(donationSummary.most_recent.date) }}
                        </span>
                    </div>
                </div>

                <div v-if="donationSummary.by_campaign" class="mt-2 space-y-1">
                    <div
                        v-for="(camp, index) in donationSummary.by_campaign"
                        :key="index"
                        class="flex items-center justify-between"
                    >
                        <span class="text-gray-500 text-xs truncate mr-2">{{ camp.name }}</span>
                        <span class="text-gray-900 text-xs font-medium flex-shrink-0">
                            ${{ Number(camp.amount).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Related records -->
            <div class="p-3 border-t border-gray-200">
                <p class="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">
                    <i class="fa-solid fa-diagram-project mr-1"></i> Related
                </p>

                <!-- Account card -->
                <div
                    v-if="related.account"
                    class="rounded bg-gray-50 p-2 mb-2"
                >
                    <p class="text-gray-900 text-xs font-medium truncate">{{ related.account.name }}</p>
                    <div class="flex items-center gap-1.5 mt-0.5">
                        <span class="text-gray-500 text-xs">Account</span>
                        <span v-if="related.account.location" class="text-gray-500 text-xs">·</span>
                        <span v-if="related.account.location" class="text-gray-500 text-xs truncate">
                            {{ related.account.location }}
                        </span>
                    </div>
                </div>

                <!-- Opportunity cards (max 3) -->
                <div
                    v-for="(opp, index) in related.opportunities.slice(0, 3)"
                    :key="index"
                    class="rounded bg-gray-50 p-2 mb-2"
                >
                    <p class="text-gray-900 text-xs font-medium truncate">{{ opp.name }}</p>
                    <div class="flex items-center justify-between mt-0.5 gap-2">
                        <span class="text-gray-500 text-xs truncate">{{ opp.stage }}</span>
                        <span v-if="opp.amount" class="text-green-400 text-xs flex-shrink-0">
                            {{ opp.amount }}
                        </span>
                    </div>
                </div>

                <!-- Nothing related -->
                <p
                    v-if="!related.account && (!related.opportunities || related.opportunities.length === 0)"
                    class="text-xs text-gray-500"
                >
                    No related records
                </p>
            </div>

            <!-- AI summary -->
            <div v-if="aiSummary" class="p-3 border-t border-gray-200">
                <p class="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">
                    <i class="fa-duotone fa-solid fa-brain-circuit mr-1"></i> What the AI used
                </p>

                <div class="space-y-1.5">
                    <div class="flex items-center justify-between">
                        <span class="text-gray-400 text-xs">Activities pulled</span>
                        <span class="text-gray-900 text-xs font-medium">
                            {{ aiSummary.activitiesPulled ?? 0 }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-400 text-xs">Related records</span>
                        <span class="text-gray-900 text-xs font-medium">
                            {{ aiSummary.relatedRecordsCount ?? 0 }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-400 text-xs">Writing profile</span>
                        <span
                            class="text-xs font-medium"
                            :class="aiSummary.writingProfileActive ? 'text-green-400' : 'text-gray-500'"
                        >
                            {{ aiSummary.writingProfileActive ? 'Active' : 'None' }}
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-400 text-xs">Email thread</span>
                        <span class="text-gray-900 text-xs font-medium">
                            {{ emailThread.length }} messages
                        </span>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-gray-400 text-xs">Donation data</span>
                        <span
                            class="text-xs font-medium"
                            :class="donationSummary ? 'text-green-400' : 'text-gray-500'"
                        >
                            {{ donationSummary ? 'Available' : 'None' }}
                        </span>
                    </div>
                </div>
            </div>

        </template>
    </div>
</template>

<script>
export default {
    name: 'ContextPanel',

    props: {
        selectedRecord: {
            type: Object,
            default: null,
        },
        activities: {
            type: Array,
            default: () => [],
        },
        related: {
            type: Object,
            default: () => ({ opportunities: [], account: null, campaigns: [] }),
        },
        loadingContext: {
            type: Boolean,
            default: false,
        },
        aiSummary: {
            type: Object,
            default: null,
        },
        emailThread: {
            type: Array,
            default: () => [],
        },
        donationSummary: {
            type: Object,
            default: null,
        },
    },

    computed: {
        initials() {
            if (!this.selectedRecord || !this.selectedRecord.name) return '?'
            return this.selectedRecord.name
                .split(' ')
                .filter(word => word.length > 0)
                .slice(0, 2)
                .map(word => word[0].toUpperCase())
                .join('')
        },
    },

    methods: {
        activityIconClass(type) {
            const map = {
                email: 'fa-solid fa-envelope',
                call: 'fa-solid fa-phone',
                event: 'fa-solid fa-calendar-day',
                task: 'fa-solid fa-check',
            }
            return map[type?.toLowerCase()] ?? 'fa-solid fa-circle'
        },

        activityLabelClass(type) {
            const map = {
                email: 'text-blue-500',
                call: 'text-amber-500',
                event: 'text-purple-500',
                task: 'text-gray-400',
            }
            return map[type?.toLowerCase()] ?? 'text-gray-400'
        },

        formatDate(dateStr) {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            if (isNaN(date.getTime())) return dateStr
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            })
        },

        leadStatusClass(status) {
            const map = {
                'New': 'bg-purple-50 text-purple-700',
                'Kick-Off': 'bg-yellow-50 text-yellow-700',
                'Accept Challenge': 'bg-orange-50 text-orange-700',
                'Registration': 'bg-green-50 text-green-700',
                'Lost - Not Registered': 'bg-red-50 text-red-700',
            }
            return map[status] ?? 'bg-gray-100 text-gray-600'
        },
    },
}
</script>
